import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { getAuth, isSuperAdmin } from '../../lib/auth';
import { MainLayout } from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import RecordRefundDrawer from '../../components/payments/RecordRefundDrawer';
import { RefundCheckboxSection, buildRefundPayload } from '../../components/payments/RefundCheckboxSection';
import OverpaymentWarning from '../../components/payments/OverpaymentWarning';
import PaidBySelect from '../../components/payments/PaidBySelect';
import MultiRefundDisplay from '../../components/payments/MultiRefundDisplay';
import ZeroPaidCheckbox from '../../components/payments/ZeroPaidCheckbox';
import AmountWithBreakdown from '../../components/payments/AmountWithBreakdown';
import useBillPaymentBreakdown from '../../components/payments/useBillPaymentBreakdown';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { NotesIcon } from '../../components/notes/NotesIcon';

import {
  FileText, Search, Filter, Loader2, ChevronDown, ChevronUp, Eye,
  DollarSign, CheckCircle2, Clock, RefreshCw, X,
  Zap, Flame, Droplets, Wifi, Trash2, CreditCard, ArrowRightLeft, Pencil,
} from 'lucide-react';

const COMMODITY_ICONS = {
  Electricity: { icon: Zap, color: 'text-amber-500' },
  Electric: { icon: Zap, color: 'text-amber-500' },
  Gas: { icon: Flame, color: 'text-orange-500' },
  Water: { icon: Droplets, color: 'text-blue-500' },
  Sewer: { icon: Droplets, color: 'text-indigo-500' },
  Telecom: { icon: Wifi, color: 'text-teal-500' },
  Internet: { icon: Wifi, color: 'text-teal-500' },
  Trash: { icon: Trash2, color: 'text-slate-500' },
  Waste: { icon: Trash2, color: 'text-slate-500' },
};

const formatCurrency = (v) => {
  if (v == null || v === 0) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
};

const formatDate = (d) => {
  if (!d) return '\u2014';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch { return d; }
};

const formatShortDate = (d) => {
  if (!d) return '';
  try { return format(new Date(d), 'MM/dd/yy'); } catch { return d; }
};

/* ───── Stats Cards ──────────────────── */
const StatsBar = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Total Bills', value: (stats.total_bills || 0).toLocaleString(), icon: FileText, color: 'bg-blue-500/15 text-blue-600' },
    { label: 'In Payment File', value: (stats.in_pf_count || 0).toLocaleString(), icon: Clock, color: 'bg-amber-500/15 text-amber-600' },
    { label: 'Paid', value: (stats.paid_count || 0).toLocaleString(), sub: formatCurrency(stats.total_paid), icon: CheckCircle2, color: 'bg-emerald-500/15 text-emerald-600' },
    { label: 'Total Due', value: formatCurrency(stats.total_amount_due), icon: DollarSign, color: 'bg-violet-500/15 text-violet-600' },
  ];
  return (
    <div className="grid grid-cols-4 gap-4" data-testid="bills-stats">
      {cards.map(({ label, value, sub, icon: Icon, color }) => (
        <div key={label} className="flex items-center gap-3 p-4 border rounded-lg bg-card">
          <div className={`flex items-center justify-center h-10 w-10 rounded-md ${color}`}><Icon className="h-5 w-5" /></div>
          <div>
            <p className="text-lg font-bold leading-tight">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
            {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ───── Filter Bar with Labels + More Accordion ──── */
const FilterBar = ({ filters, filterValues, onFilterChange, onClear }) => {
  const [showMore, setShowMore] = useState(false);
  const hasActive = Object.values(filterValues).some(v => v);
  const activeCount = Object.values(filterValues).filter(v => v).length;

  return (
    <div className="border rounded-lg bg-muted/20" data-testid="bills-filters">
      {/* Primary filters — always visible */}
      <div className="flex flex-wrap items-end gap-3 p-3">
        <Filter className="h-4 w-4 text-muted-foreground mt-5" />

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Property</Label>
          <div className="relative w-48">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search property..."
              className="h-8 pl-7 text-xs"
              value={filterValues.property_search || ''}
              onChange={e => onFilterChange('property_search', e.target.value)}
              data-testid="filter-property"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Bill ID</Label>
          <div className="relative w-48">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by BillId"
              className="h-8 pl-7 text-xs font-mono"
              value={filterValues.bill_id || ''}
              onChange={e => onFilterChange('bill_id', e.target.value)}
              data-testid="filter-bill-id"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Vendor</Label>
          <Select value={filterValues.vendor || '_all'} onValueChange={v => onFilterChange('vendor', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-vendor"><SelectValue placeholder="All Vendors" /></SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="_all">All Vendors</SelectItem>
              {(filters.vendors || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Commodity</Label>
          <Select value={filterValues.commodity || '_all'} onValueChange={v => onFilterChange('commodity', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-commodity"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              {(filters.commodities || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Unit Type</Label>
          <Select value={filterValues.unit_type || '_all'} onValueChange={v => onFilterChange('unit_type', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-unit-type"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              <SelectItem value="unit">Unit</SelectItem>
              <SelectItem value="common_area">Common Area</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Status</Label>
          <Select value={filterValues.payment_status || '_all'} onValueChange={v => onFilterChange('payment_status', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-payment-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="refund">Refund</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {(filters.refund_to_options || []).length > 0 && (
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Refund To</Label>
            <Select value={filterValues.refund_to || '_all'} onValueChange={v => onFilterChange('refund_to', v === '_all' ? '' : v)}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-refund-to"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Recipients</SelectItem>
                {(filters.refund_to_options || []).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Drift / Unaccounted</Label>
          <Select value={filterValues.drift_filter || '_all'} onValueChange={v => onFilterChange('drift_filter', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-drift"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All (Drift + OK)</SelectItem>
              <SelectItem value="unaccounted">Unaccounted (Drift)</SelectItem>
              <SelectItem value="reconciled">Reconciled only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* More toggle */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs gap-1 text-muted-foreground hover:text-foreground"
          onClick={() => setShowMore(p => !p)}
          data-testid="filter-more-toggle"
        >
          {showMore ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          More{activeCount > 0 && !showMore ? ` (${activeCount})` : ''}
        </Button>

        {hasActive && (
          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-red-500 hover:text-red-700" onClick={onClear} data-testid="filter-clear">
            <X className="h-3 w-3" /> Clear All
          </Button>
        )}
      </div>

      {/* Expanded filters */}
      {showMore && (
        <div className="flex flex-wrap items-end gap-3 px-3 pb-3 pt-1 border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">State</Label>
            <Select value={filterValues.state || '_all'} onValueChange={v => onFilterChange('state', v === '_all' ? '' : v)}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-state"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="_all">All States</SelectItem>
                {(filters.states || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Account #</Label>
            <Input
              placeholder="Search..."
              className="h-8 w-36 text-xs"
              value={filterValues.account_number || ''}
              onChange={e => onFilterChange('account_number', e.target.value)}
              data-testid="filter-account-number"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Issue Date From</Label>
            <Input
              type="date"
              className="h-8 w-36 text-xs"
              value={filterValues.date_from || ''}
              onChange={e => onFilterChange('date_from', e.target.value)}
              data-testid="filter-date-from"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Issue Date To</Label>
            <Input
              type="date"
              className="h-8 w-36 text-xs"
              value={filterValues.date_to || ''}
              onChange={e => onFilterChange('date_to', e.target.value)}
              data-testid="filter-date-to"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ───── Payment Status Badge ─────────── */
const PaymentBadge = ({ bill, breakdown }) => {
  const { payment_status, paid_amount, refund, refund_amount, transaction_reference } = bill;
  const bd = breakdown || { paid_breakdown: {}, paid_count: 0, paid_total: 0,
                            refund_breakdown: {}, refund_count: 0, refund_total: 0 };

  if (refund && ['yes', 'Yes', 'YES'].includes(refund)) {
    const totalRfd = bd.refund_total > 0 ? bd.refund_total : (refund_amount || 0);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col items-start gap-0.5 cursor-help">
            <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px]" data-testid="badge-refund">
              Refund
            </Badge>
            <AmountWithBreakdown
              amount={totalRfd}
              breakdown={bd.refund_breakdown}
              count={bd.refund_count}
              label="Refund"
              testId={`bill-refund-breakdown-${bill.bill_id}`}
              amountClassName="text-[10px] text-red-700"
            />
          </div>
        </TooltipTrigger>
        <TooltipContent className="text-xs">
          <p>Refunded: {formatCurrency(totalRfd)}</p>
          {transaction_reference && <p>Ref: {transaction_reference}</p>}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (payment_status === 'paid' || (paid_amount && paid_amount > 0)) {
    const totalPaid = bd.paid_total > 0 ? bd.paid_total : (paid_amount || 0);
    return (
      <div className="flex flex-col items-start gap-0.5">
        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]" data-testid="badge-paid">
          Paid
        </Badge>
        {totalPaid > 0 && (
          <AmountWithBreakdown
            amount={totalPaid}
            breakdown={bd.paid_breakdown}
            count={bd.paid_count}
            label="Paid"
            testId={`bill-paid-breakdown-${bill.bill_id}`}
            amountClassName="text-[10px] text-emerald-700"
          />
        )}
      </div>
    );
  }

  return (
    <Badge variant="outline" className="bg-slate-500/10 text-slate-500 border-slate-500/20 text-[10px]" data-testid="badge-unpaid">
      Unpaid
    </Badge>
  );
};

const PF_STATUS_COLORS = {
  review: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  approved: 'bg-sky-500/10 text-sky-700 border-sky-500/30',
  paid: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
};

const PfStatusPill = ({ status }) => {
  if (!status) return null;
  const s = String(status).toLowerCase();
  const cls = PF_STATUS_COLORS[s] || 'bg-slate-500/10 text-slate-600 border-slate-500/30';
  return (
    <Badge variant="outline" className={`text-[9px] px-1 py-0 ${cls}`} data-testid="pf-status-pill">
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </Badge>
  );
};

/* ───── Bill Row ─────────────────────── */
const BillRow = ({ bill, breakdown, onView, onPay, onRefund }) => {
  const hasPf = !!bill.payment_file_id;
  const isPaid = bill.payment_status === 'paid' || (bill.paid_amount && bill.paid_amount > 0);
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = !isPaid && bill.due_date && bill.due_date < today;
  return (
    <TableRow
      className={`hover:bg-muted/50 transition-colors text-sm ${isOverdue ? 'bg-red-50/40' : ''}`}
      data-testid={`bill-row-${bill.bill_id}`}
    >
      {/* Property + Service Address */}
      <TableCell className="max-w-[220px]">
        <div className="flex flex-col">
          <span className="font-medium text-xs truncate" title={bill.primary_service_address}>
            {bill.primary_service_address || bill.customer_full_name || '\u2014'}
          </span>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-[10px] text-muted-foreground truncate">
              {bill.primary_service_city}{bill.primary_service_state ? `, ${bill.primary_service_state}` : ''} {bill.primary_service_zip}
            </span>
            {bill.is_unit_bill ? (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 bg-indigo-500/10 text-indigo-600 border-indigo-500/20">Unit</Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Unit-level bill</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 bg-slate-500/10 text-slate-500 border-slate-500/20">CA</Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Common Area</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </TableCell>
      {/* Dates */}
      <TableCell className="text-xs whitespace-nowrap">
        <div className="flex flex-col gap-0.5">
          <span>{formatShortDate(bill.invoice_date)}</span>
          <div className="flex items-center gap-1">
            <span className={`text-[10px] ${isOverdue ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
              Due {formatShortDate(bill.due_date)}
            </span>
            {isOverdue && (
              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-red-500/10 text-red-700 border-red-500/30" data-testid={`overdue-badge-${bill.bill_id}`}>
                Overdue
              </Badge>
            )}
          </div>
        </div>
      </TableCell>
      {/* Period */}
      <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
        {bill.period_from && bill.period_to ? (
          <span>{formatShortDate(bill.period_from)} — {formatShortDate(bill.period_to)}</span>
        ) : '\u2014'}
      </TableCell>
      {/* PF Batch */}
      <TableCell>
        {bill.pf_batch_name ? (
          <div className="flex flex-col items-start gap-0.5">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-500/10 text-blue-600 border-blue-500/20" data-testid={`bill-pf-${bill.bill_id}`}>
              {bill.pf_batch_name}
            </Badge>
            <PfStatusPill status={bill.pf_status} />
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground">Not in PF</span>
        )}
      </TableCell>
      {/* Vendor + Account */}
      <TableCell className="max-w-[160px]">
        <div className="flex flex-col">
          <span className="text-xs font-medium truncate" title={bill.vendor_name}>{bill.vendor_name || '\u2014'}</span>
          <span className="text-[10px] text-muted-foreground font-mono truncate" title={bill.account_number}>{bill.account_number}</span>
        </div>
      </TableCell>
      {/* Commodity */}
      <TableCell className="text-xs">
        {(() => {
          const cm = bill.commodity || '';
          const match = Object.entries(COMMODITY_ICONS).find(([k]) => cm.toLowerCase().includes(k.toLowerCase()));
          if (match) {
            const [, { icon: Icon, color }] = match;
            return <span className="flex items-center gap-1"><Icon className={`h-3.5 w-3.5 ${color}`} />{cm}</span>;
          }
          return cm || '\u2014';
        })()}
      </TableCell>
      {/* New Charges */}
      <TableCell className="text-xs font-mono text-right">{formatCurrency(bill.current_bill_amount)}</TableCell>
      {/* Total Due */}
      <TableCell className="text-xs font-mono text-right font-medium">{formatCurrency(bill.total_amount_due)}</TableCell>
      {/* Payment Status */}
      <TableCell><PaymentBadge bill={bill} breakdown={breakdown} /></TableCell>
      {/* Actions */}
      <TableCell>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onView(bill)} data-testid={`bill-view-${bill.bill_id}`}>
                <Eye className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">View details</TooltipContent>
          </Tooltip>
          {hasPf && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => onPay(bill)} className="p-1 rounded hover:bg-emerald-100 transition-colors" data-testid={`bill-pay-${bill.bill_id}`}>
                    <CreditCard className="h-3.5 w-3.5 text-emerald-600" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="text-xs">{bill.payment_status === 'paid' ? 'View Payment' : 'Record Payment'}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => onRefund(bill)} className="p-1 rounded hover:bg-red-100 transition-colors" data-testid={`bill-refund-${bill.bill_id}`}>
                    <ArrowRightLeft className="h-3.5 w-3.5 text-red-500" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Record Refund</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

/* ───── Main Page ────────────────────── */
export default function BillsPage() {
  const { user } = getAuth();
  const navigate = useNavigate();

  const [currentAccount, setCurrentAccount] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });

  useEffect(() => {
    const check = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (acc?.id && acc.id !== currentAccount?.id) setCurrentAccount(acc);
    };
    check();
    const iv = setInterval(check, 1000);
    const to = setTimeout(() => clearInterval(iv), 10000);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [currentAccount?.id]);

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ vendors: [], commodities: [], states: [], properties: [] });
  const [searchParams] = useSearchParams();
  const [filterValues, setFilterValues] = useState(() => {
    const init = {};
    ['property_search', 'account_number', 'vendor', 'commodity', 'state', 'unit_type', 'payment_status', 'refund_to', 'drift_filter', 'date_from', 'date_to'].forEach(k => {
      const v = searchParams.get(k);
      if (v) init[k] = v;
    });
    return init;
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const limit = 50;

  // Payment drawer state
  const [payDrawerOpen, setPayDrawerOpen] = useState(false);
  const [payBill, setPayBill] = useState(null);
  const [payForm, setPayForm] = useState({});
  const [paySaving, setPaySaving] = useState(false);
  const [paySummary, setPaySummary] = useState(null);
  const [paySummaryLoading, setPaySummaryLoading] = useState(false);
  const [zeroPaid, setZeroPaid] = useState(false);
  const [zeroPaidReason, setZeroPaidReason] = useState('');
  // Per-row transaction edit/delete state (mirrors PaymentsBillsWorkspace UX)
  const [editingTxnId, setEditingTxnId] = useState(null);
  const [deletingTxnId, setDeletingTxnId] = useState(null);
  const [refundRefreshKey, setRefundRefreshKey] = useState(0);

  // Refund drawer state
  const [refundDrawerOpen, setRefundDrawerOpen] = useState(false);
  const [refundBill, setRefundBill] = useState(null);
  const [refundSection, setRefundSection] = useState({ enabled: false, refund_amount: '', refund_to: '', notes: '' });

  const userId = user?.id || user?.user_id || '';
  const userName = user?.name || '';

  const accountId = currentAccount?.id;

  // Per-bill paid/refund breakdown for tooltip + dot indicator
  const { get: getBreakdown } = useBillPaymentBreakdown(
    bills.map(b => b.bill_id),
    accountId,
  );

  // Fetch filters once
  useEffect(() => {
    if (!accountId) return;
    api.get('/bills/filters', { params: { account_id: accountId } })
      .then(({ data }) => setFilters(data))
      .catch(() => {});
  }, [accountId]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get('/bills/stats', { params: { account_id: accountId } });
      setStats(data);
    } catch { /* silent */ }
  }, [accountId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Fetch bills
  const fetchBills = useCallback(async (pageNum = 1, append = false) => {
    if (!accountId) return;
    if (!append) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { account_id: accountId, page: pageNum, limit };
      Object.entries(filterValues).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get('/bills/list', { params });
      if (append) {
        setBills(prev => [...prev, ...(data.bills || [])]);
      } else {
        setBills(data.bills || []);
      }
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      setPage(pageNum);
    } catch {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [accountId, filterValues]);

  useEffect(() => { fetchBills(1); }, [fetchBills]);

  const handleFilterChange = (key, value) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilterValues({});
  };

  const handleLoadMore = () => {
    fetchBills(page + 1, true);
  };

  const handleView = (bill) => {
    navigate(`/bills/${bill.bill_id}?account_id=${accountId}`);
  };

  // ── Payment drawer ──
  const openPayDrawer = async (bill) => {
    if (!bill.payment_file_id) {
      toast.error('This bill is not in a Payment File. Pay/Refund is only available for PF bills.');
      return;
    }
    setPayBill(bill);
    setPayForm({
      transaction_reference: '',
      amount: bill.total_amount_due || '',
      payment_method: '',
      notes: '',
      paid_by_name: 'BillPortal',
      transaction_date: new Date().toISOString().split('T')[0],
    });
    setPayDrawerOpen(true);
    setRefundSection({ enabled: false, refund_amount: '', refund_to: '', notes: '' });
    setZeroPaid(false);
    setZeroPaidReason('');
    setEditingTxnId(null);
    setPaySummary(null);
    setPaySummaryLoading(true);
    try {
      const { data } = await api.get('/payments/v2/workspace/bill-payment-summary', {
        params: { bill_id: bill.bill_id, payment_file_id: bill.payment_file_id, account_id: bill.account_id },
      });
      setPaySummary(data);
      // Pre-fill remaining amount if partially paid
      if (data.remaining > 0.01) {
        setPayForm(p => ({ ...p, amount: data.remaining.toFixed(2) }));
      }
    } catch { /* silent */ }
    finally { setPaySummaryLoading(false); }
  };

  // Reload payment summary + bill list after txn mutation
  const refreshPaymentDrawer = async () => {
    if (!payBill) return;
    try {
      const { data } = await api.get('/payments/v2/workspace/bill-payment-summary', {
        params: { bill_id: payBill.bill_id, payment_file_id: payBill.payment_file_id, account_id: payBill.account_id },
      });
      setPaySummary(data);
    } catch { /* silent */ }
    fetchBills(1);
    fetchStats();
  };

  // Populate form from a specific txn for in-place edit
  const editSpecificTxn = (txn) => {
    if (!txn) return;
    setEditingTxnId(txn.id);
    setPayForm({
      transaction_reference: txn.transaction_reference || '',
      amount: txn.amount != null ? String(txn.amount) : '',
      payment_method: txn.payment_method || '',
      notes: txn.notes || '',
      paid_by_name: txn.paid_by_name || 'BillPortal',
      transaction_date: txn.transaction_date ? String(txn.transaction_date).split('T')[0] : '',
    });
    setZeroPaid(false);
    setRefundSection({ enabled: false, refund_amount: '', refund_to: '', notes: '' });
  };

  const deleteSpecificTxn = async (txn) => {
    if (!txn?.id || !payBill) return;
    const ok = window.confirm(
      `Delete transaction ${txn.transaction_reference || `#${String(txn.id).slice(0, 8)}`} (${formatCurrency(txn.amount)} by ${txn.paid_by_name || '—'})? This cannot be undone from the UI.`
    );
    if (!ok) return;
    setDeletingTxnId(txn.id);
    try {
      await api.delete(`/payments/v2/delete-transaction/${txn.id}`, {
        params: { account_id: payBill.account_id, user_id: userId, user_name: userName },
      });
      toast.success('Transaction deleted');
      await refreshPaymentDrawer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingTxnId(null);
    }
  };

  const savePayment = async () => {
    // Zero-paid path: bypass txn entirely, just mark the bill paid with $0.
    if (zeroPaid) {
      setPaySaving(true);
      try {
        await api.post(
          `/payments/v2/files/${payBill.payment_file_id}/mark-paid-zero`,
          null,
          { params: {
              bill_id: payBill.bill_id,
              account_id: payBill.account_id,
              reason: zeroPaidReason || 'No payment needed ($0 due or credits cover it)',
              user_id: userId,
              user_name: userName,
            },
          },
        );
        toast.success('Bill marked as paid ($0 — no transaction)');
        setPayDrawerOpen(false);
        setPayBill(null);
        setZeroPaid(false);
        setZeroPaidReason('');
        fetchBills(1);
        fetchStats();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to mark bill paid ($0)');
      } finally {
        setPaySaving(false);
      }
      return;
    }

    if (!payForm.transaction_reference?.trim()) { toast.error('Transaction reference required'); return; }
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error('Valid amount required'); return; }

    // ── EDIT path: when editingTxnId is set, PATCH the specific transaction ──
    if (editingTxnId) {
      setPaySaving(true);
      try {
        await api.patch(`/payments/v2/transactions/${editingTxnId}`, {
          amount: parseFloat(payForm.amount),
          transaction_reference: payForm.transaction_reference,
          payment_method: payForm.payment_method || '',
          notes: payForm.notes || '',
          paid_by_name: payForm.paid_by_name || 'BillPortal',
          transaction_date: payForm.transaction_date || '',
        }, {
          params: { account_id: payBill.account_id, user_id: userId, user_name: userName },
        });
        toast.success('Transaction updated');
        setEditingTxnId(null);
        await refreshPaymentDrawer();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Update failed');
      } finally {
        setPaySaving(false);
      }
      return;
    }

    // Full-payment confirmation
    try {
      const { data: latest } = await api.get('/payments/v2/workspace/bill-payment-summary', {
        params: { bill_id: payBill.bill_id, payment_file_id: payBill.payment_file_id, account_id: payBill.account_id },
      });
      setPaySummary(latest);
      if (latest.is_fully_paid) {
        const ok = window.confirm(
          `This bill is already fully paid (${(latest.txn_sum || 0).toFixed(2)} of ${(latest.payment_amount || 0).toFixed(2)} via ${latest.transaction_count} transaction(s)).\n\nAre you sure you want to record ANOTHER transaction?`
        );
        if (!ok) return;
      }
    } catch { /* continue */ }

    setPaySaving(true);
    try {
      const utilRes = await api.get(`/payments/v2/files/${payBill.payment_file_id}/utilities`, {
        params: { account_id: payBill.account_id, account_number: payBill.account_number, limit: 10 }
      });
      const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === payBill.bill_id);
      if (!matchUtil) { toast.error('Could not find utility record for this bill'); setPaySaving(false); return; }

      await api.post(`/payments/v2/files/${payBill.payment_file_id}/record-payment`, {
        account_id: payBill.account_id,
        utility_id: matchUtil.payment_file_utility_id,
        transaction_reference: payForm.transaction_reference,
        amount: parseFloat(payForm.amount),
        payment_method: payForm.payment_method || '',
        notes: payForm.notes || '',
        paid_by: userId,
        paid_by_name: payForm.paid_by_name || 'BillPortal',
        created_by: userId,
        created_by_name: userName,
        transaction_date: payForm.transaction_date || '',
      });

      // Chained refund: see RefundCheckboxSection
      const refundPayload = buildRefundPayload({
        value: refundSection,
        accountId: payBill.account_id,
        utilityId: matchUtil.payment_file_utility_id,
        userId,
        userName,
      });
      if (refundPayload) {
        try {
          await api.post(`/payments/v2/files/${payBill.payment_file_id}/record-refund`, refundPayload);
          toast.success('Payment recorded · Refund logged');
        } catch (rerr) {
          toast.error('Payment saved, but refund failed: ' + (rerr?.response?.data?.detail || 'Unknown error'));
        }
      } else {
        toast.success('Payment recorded');
      }
      setRefundSection({ enabled: false, refund_amount: '', refund_to: '', notes: '' });
      setPayDrawerOpen(false);
      setPayBill(null);
      fetchBills(1);
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
    finally { setPaySaving(false); }
  };

  // ── Refund drawer ──
  // Drawer UI + save/clear is handled by the shared RecordRefundDrawer
  // component (see import). We only manage open/close + selected bill here.
  const openRefundDrawer = (bill) => {
    if (!bill.payment_file_id) {
      toast.error('This bill is not in a Payment File. Pay/Refund is only available for PF bills.');
      return;
    }
    setRefundBill(bill);
    setRefundDrawerOpen(true);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/bills/sync/trigger', null, { params: { mode: 'incremental' } });
      toast.success(`Bill sync complete: ${data.bills_created} new, ${data.bills_updated} updated`);
      fetchBills(1);
      fetchStats();
    } catch {
      toast.error('Bill sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (!accountId) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <FileText className="h-12 w-12 mb-4 opacity-40" />
          <p className="text-sm font-medium">No account selected</p>
          <p className="text-xs mt-1">Use the account switcher in the header to select an organization</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
      <div className="space-y-5" data-testid="bills-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-foreground" strokeWidth={2} />
            <h1 className="text-2xl font-bold tracking-tight">Bills</h1>
            <NotesIcon pageKey="bills" pageLabel="Bills" accountId={currentAccount?.id} />
            <span className="text-sm text-muted-foreground">
              {total.toLocaleString()} bills
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin() && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleSync} disabled={syncing} data-testid="bills-sync-btn">
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Bills'}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <StatsBar stats={stats} />

        {/* Filters */}
        <FilterBar
          filters={filters}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
        />

        {/* Table */}
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="bills-table-container">
          {loading ? (
            <div className="space-y-3 p-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : bills.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText className="h-12 w-12 mb-3 opacity-40" />
              <p className="text-sm">No bills found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table data-testid="bills-table">
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[220px]">Property / Address</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Issue / Due</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Period</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">PF Batch</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[160px]">Vendor / Account</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Commodity</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">New Charges</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">Total Due</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Payment</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[90px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bills.map(b => <BillRow key={b.bill_id} bill={b} breakdown={getBreakdown(b.bill_id)} onView={handleView} onPay={openPayDrawer} onRefund={openRefundDrawer} />)}
                  </TableBody>
                </Table>
              </div>
              {/* Load More */}
              {hasMore && (
                <div className="flex justify-center py-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs gap-1.5"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    data-testid="bills-load-more"
                  >
                    {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {loadingMore ? 'Loading...' : `Load More (${bills.length} of ${total})`}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </TooltipProvider>

      {/* Payment Drawer */}
      <Sheet open={payDrawerOpen} onOpenChange={(o) => { if (!o) { setPayDrawerOpen(false); setPayBill(null); } }}>
        <SheetContent side="right" className="sm:max-w-md w-[420px] flex flex-col p-0" data-testid="pay-drawer">
          <SheetHeader className="px-5 pt-5 pb-3 border-b">
            <SheetTitle className="text-sm flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-emerald-600" />
              Record Payment
            </SheetTitle>
          </SheetHeader>
          {payBill && (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="bg-muted/30 rounded p-3 text-xs space-y-1">
                <p className="font-medium">{payBill.vendor_name} — {payBill.account_number}</p>
                <p className="text-muted-foreground">{payBill.primary_service_address}</p>
                <p>Total Due: <strong>{formatCurrency(payBill.total_amount_due)}</strong></p>
              </div>

              {/* Payment summary (stored vs computed) */}
              {paySummaryLoading ? (
                <div className="flex items-center justify-center py-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 mr-2 animate-spin" /> Loading payment history…</div>
              ) : paySummary && (
                <div className="border rounded-lg p-3 text-xs space-y-1.5 bg-muted/20" data-testid="bills-pay-summary">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-[10px] uppercase tracking-wider font-semibold">Stored / Computed</span>
                    <div className="flex gap-1">
                      <Badge variant="outline" className="text-[10px]">{paySummary.stored_status}</Badge>
                      <Badge variant="outline" className="text-[10px]">{paySummary.computed_status}</Badge>
                    </div>
                  </div>
                  <div className="text-muted-foreground">
                    Paid <span className="font-mono font-semibold text-emerald-700">{formatCurrency(paySummary.txn_sum)}</span> of {formatCurrency(paySummary.payment_amount)}
                    {paySummary.remaining > 0.01 && <> · Remaining <span className="font-mono text-amber-700">{formatCurrency(paySummary.remaining)}</span></>}
                  </div>
                  {paySummary.status_mismatch && (
                    <div className="text-amber-700 text-[11px] pt-1 border-t">⚠ Stored status disagrees with transactions</div>
                  )}
                  {paySummary.transactions && paySummary.transactions.length > 0 && (
                    <div className="pt-1.5 border-t space-y-1">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                        Transaction History ({paySummary.transactions.length})
                      </p>
                      {paySummary.transactions.map((t, i) => (
                        <div
                          key={t.id || i}
                          className="flex items-center justify-between gap-2 text-[11px] border border-border rounded px-2 py-1 bg-background"
                          data-testid={`bp-txn-row-${i}`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="text-muted-foreground font-mono text-[10px]">#{i + 1}</span>
                            <span className="font-mono font-semibold text-emerald-700">{formatCurrency(t.amount)}</span>
                            <span className="text-muted-foreground truncate" title={t.transaction_reference}>{t.transaction_reference || '\u2014'}</span>
                            <span className="text-muted-foreground shrink-0">· {t.paid_by_name || '\u2014'}</span>
                            {t.transaction_date && (
                              <span className="text-muted-foreground shrink-0 text-[10px]">· {formatShortDate(t.transaction_date)}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost" size="sm" className="h-6 w-6 p-0"
                              onClick={() => editSpecificTxn(t)}
                              title="Edit this transaction"
                              data-testid={`bp-txn-edit-${i}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                              onClick={() => deleteSpecificTxn(t)}
                              disabled={deletingTxnId === t.id}
                              title="Delete this transaction"
                              data-testid={`bp-txn-delete-${i}`}
                            >
                              {deletingTxnId === t.id
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <Trash2 className="h-3 w-3" />}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Multi-refund display (bill.refund + pm_refund_events) */}
              <MultiRefundDisplay
                billId={payBill?.bill_id}
                accountId={payBill?.account_id}
                primaryRefund={payBill?.refund === 'yes' || Number(payBill?.refund_amount || 0) > 0.01 ? {
                  refund_amount: payBill?.refund_amount,
                  refund_to: payBill?.refund_to,
                  refund_completed: !!payBill?.refund_completed,
                  refund_date: payBill?.refund_date,
                } : null}
                onEditPrimary={() => {
                  setRefundBill(payBill);
                  setRefundDrawerOpen(true);
                }}
                onDeletePrimary={async () => {
                  const ok = window.confirm(
                    `Delete primary refund of ${formatCurrency(payBill?.refund_amount || 0)} to ${payBill?.refund_to || '—'}? This will clear the bill-level refund.`
                  );
                  if (!ok) throw new Error('cancelled');
                  let utility_id = payBill?.payment_file_utility_id || payBill?.utility_id;
                  if (!utility_id && payBill?.payment_file_id) {
                    try {
                      const res = await api.get(`/payments/v2/files/${payBill.payment_file_id}/utilities`, {
                        params: { account_id: payBill.account_id, account_number: payBill.account_number, limit: 50 },
                      });
                      const m = (res.data?.utilities || []).find((u) => u.bill_id === payBill.bill_id);
                      utility_id = m?.payment_file_utility_id || null;
                    } catch {/* ignore */}
                  }
                  if (!utility_id) { toast.error('Could not find utility record'); throw new Error('no_utility'); }
                  await api.post(`/payments/v2/files/${payBill.payment_file_id}/clear-refund`, {
                    account_id: payBill.account_id,
                    utility_id,
                    refund_amount: 0,
                    user_id: userId,
                    user_name: userName,
                  });
                  toast.success('Primary refund cleared');
                  setPayBill((b) => b ? { ...b, refund: 'no', refund_amount: 0, refund_to: '', refund_completed: false } : b);
                  fetchBills(1);
                  fetchStats();
                }}
                onEditEvent={async (ev) => {
                  const newAmt = window.prompt(
                    `Edit refund amount for ${ev.refund_to}:\n(Current: $${Number(ev.refund_amount).toFixed(2)})`,
                    String(ev.refund_amount || ''),
                  );
                  if (newAmt == null) return;
                  const v = parseFloat(newAmt);
                  if (!(v > 0)) { toast.error('Amount must be > 0'); return; }
                  try {
                    await api.patch(`/payments/v2/refund-events/${ev.id}`, {
                      refund_amount: v,
                    }, {
                      params: { account_id: payBill.account_id, user_id: userId, user_name: userName },
                    });
                    toast.success('Refund updated');
                    setRefundRefreshKey((k) => k + 1);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Update failed');
                  }
                }}
                onDeleteEvent={async (ev) => {
                  const ok = window.confirm(
                    `Delete refund of $${Number(ev.refund_amount).toFixed(2)} to ${ev.refund_to}?`
                  );
                  if (!ok) throw new Error('cancelled');
                  await api.delete(`/payments/v2/refund-events/${ev.id}`, {
                    params: { account_id: payBill.account_id, user_id: userId, user_name: userName },
                  });
                  toast.success('Refund deleted');
                }}
                refreshKey={payDrawerOpen ? `${payBill?.bill_id}-${refundRefreshKey}` : null}
              />

              {/* $0 paid (no payment needed) toggle — R1 pattern from operator */}
              <ZeroPaidCheckbox
                checked={zeroPaid}
                onChange={(v) => {
                  setZeroPaid(v);
                  if (v) {
                    // Clear the form when toggled on
                    setPayForm(p => ({ ...p, amount: '0', transaction_reference: '$0_due' }));
                  }
                }}
                reason={zeroPaidReason}
                onReasonChange={setZeroPaidReason}
                testId="bills-zero-paid"
              />

              <div className={`space-y-3 ${zeroPaid ? 'opacity-50 pointer-events-none' : ''}`}>
                <div>
                  <Label className="text-xs">Transaction Reference *</Label>
                  <Input className="h-9 text-sm mt-1" value={payForm.transaction_reference || ''} onChange={e => setPayForm(p => ({...p, transaction_reference: e.target.value}))} placeholder="e.g. CHK-12345" data-testid="pay-txn-ref" />
                </div>
                <div>
                  <Label className="text-xs">Amount *</Label>
                  <Input className="h-9 text-sm mt-1 font-mono" type="number" step="0.01" value={payForm.amount || ''} onChange={e => setPayForm(p => ({...p, amount: e.target.value}))} data-testid="pay-amount" />
                  <OverpaymentWarning
                    paidAmount={payForm.amount}
                    requestedAmount={payBill?.payment_amount || payBill?.total_amount_due}
                    existingPaid={paySummary?.txn_sum || 0}
                    testId="bills-pay-overpay-warning"
                  />
                </div>
                <div>
                  <Label className="text-xs">Payment Method</Label>
                  <Input className="h-9 text-sm mt-1" value={payForm.payment_method || ''} onChange={e => setPayForm(p => ({...p, payment_method: e.target.value}))} placeholder="ACH, Check, Wire" data-testid="pay-method" />
                </div>
                <div>
                  <Label className="text-xs">Paid By *</Label>
                  <div className="mt-1">
                    <PaidBySelect
                      value={payForm.paid_by_name || 'BillPortal'}
                      onChange={v => setPayForm(p => ({ ...p, paid_by_name: v }))}
                      customerLabel={payBill?.customer_name || ''}
                      testId="bills-pay-paid-by"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Transaction Date</Label>
                  <Input className="h-9 text-sm mt-1" type="date" value={payForm.transaction_date || ''} onChange={e => setPayForm(p => ({...p, transaction_date: e.target.value}))} data-testid="pay-date" />
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Input className="h-9 text-sm mt-1" value={payForm.notes || ''} onChange={e => setPayForm(p => ({...p, notes: e.target.value}))} placeholder="Optional notes" data-testid="pay-notes" />
                </div>
              </div>
              <div className={`mt-3 ${zeroPaid ? 'opacity-50 pointer-events-none' : ''}`}>
                <RefundCheckboxSection
                  paidByName={payForm.paid_by_name || 'BillPortal'}
                  paidAmount={payForm.amount}
                  requestedAmount={payBill?.payment_amount || payBill?.total_amount_due}
                  value={refundSection}
                  onChange={setRefundSection}
                />
              </div>
            </div>
          )}
          <div className="border-t px-5 py-3 flex items-center justify-end gap-2">
            {editingTxnId && (
              <div className="flex items-center gap-2 text-xs text-blue-700 mr-auto">
                <Pencil className="h-3 w-3" />
                Editing existing transaction
                <Button
                  variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                  onClick={() => {
                    setEditingTxnId(null);
                    setPayForm({
                      transaction_reference: '',
                      amount: payBill?.total_amount_due || '',
                      payment_method: '',
                      notes: '',
                      paid_by_name: 'BillPortal',
                      transaction_date: new Date().toISOString().split('T')[0],
                    });
                  }}
                  data-testid="bp-cancel-edit-btn"
                >
                  Cancel edit
                </Button>
              </div>
            )}
            <Button variant="outline" size="sm" className="text-xs" onClick={() => { setPayDrawerOpen(false); setPayBill(null); setEditingTxnId(null); }}>Cancel</Button>
            <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={savePayment} disabled={paySaving} data-testid="pay-save-btn">
              {paySaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : (editingTxnId ? <Pencil className="h-3 w-3 mr-1" /> : <CreditCard className="h-3 w-3 mr-1" />)}
              {zeroPaid ? 'Mark Paid ($0)' : (editingTxnId ? 'Update Transaction' : 'Save Payment')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Refund Drawer (shared component) */}
      <RecordRefundDrawer
        open={refundDrawerOpen}
        onOpenChange={(o) => { setRefundDrawerOpen(o); if (!o) setRefundBill(null); }}
        bill={refundBill}
        userId={userId}
        userName={userName}
        partnerName={currentAccount?.partner_name || currentAccount?.bp_partner_name || null}
        onSaved={() => { fetchBills(1); fetchStats(); }}
        onCleared={() => { fetchBills(1); fetchStats(); }}
      />
    </MainLayout>
  );
}
